<script setup lang="ts">
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { literatureSchema } from '~/utils/crystallize/literature'

type Literature = NonNullable<CrystallizeProduct['components']['literature']>[number]

const props = defineProps<{ data: Literature }>()

const itemQueries = useQueries({
  queries: (props.data.items ?? []).map(item => useCatalogueQueryOptions({ path: item.path, schema: literatureSchema })),
})

const controlsClass = [
  'w-8',
  'fill-blurple-100',
  'text-blurple-500',
  'group-hover:fill-blurple-50',
  'active:fill-blurple-200',
  'group-disabled:fill-carbon-100',
  'group-disabled:text-carbon-400',
  'group-disabled:cursor-not-allowed',
]
</script>

<template>
  <section>
    <ContentGrid v-if="data.metadata">
      <div class="col-span-4 2xl:col-span-6 2xl:col-start-2 flex flex-col gap-m mb-3xl">
        <h2 v-text="data.metadata.title" />
        <CrystallizeRichText v-for="(block, bKey) in data.metadata.text" :key="bKey" :block="block" class="font-heading text-2xl font-light" />
      </div>
    </ContentGrid>
    <ContentGrid v-if="itemQueries.length" class="[--gap:var(--gutter)] [--item-width:max(20%,220px)]">
      <Carousel :slides-to-scroll="1" class="full-width-content" variant="nettbutikk" controls :data="itemQueries">
        <template #default="{ item: query }">
          <div v-if="query?.data" class="bg-white rounded-lg items-center h-full py-m px-s flex flex-col gap-m">
            <CrystallizeImage :image="query.data.components.cover" img-class="h-44" />
            <div class="flex flex-col text-center gap-3xs">
              <strong class="text-lg font-normal" v-text="query.data.components.title" />
              <span class="font-light text-base" v-text="query.data.components.edition" />
            </div>
          </div>
        </template>

        <!-- Custom hexagon buttons -->
        <template #prev-button="{ scrollPrev, canScroll }">
          <button class="group" :disabled="!canScroll" aria-label="Forrige" @click="scrollPrev">
            <Shape variant="hexagon" :class="controlsClass" :icon="faArrowLeft" />
          </button>
        </template>
        <template #next-button="{ scrollNext, canScroll }">
          <button class="group" :disabled="!canScroll" aria-label="Neste" @click="scrollNext">
            <Shape variant="hexagon" :class="controlsClass" :icon="faArrowRight" />
          </button>
        </template>
      </Carousel>
    </ContentGrid>
  </section>
</template>
